/**
 * GDPR Data Export Component
 * User interface for GDPR-compliant data export
 */

import { CheckCircle, Download, FileText, Info, Shield } from "lucide-react";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { gdprService } from "../../services/gdpr.service";

/**
 * GDPR Article 30 record of processing activities, as returned by
 * GET /api/gdpr/processing-record (backend gdpr-export.service.ts).
 *
 * This was previously declared as `{ id, type, status, createdAt }` - four
 * fields the endpoint has never sent - while the component rendered the real
 * ones. @ts-nocheck kept the contradiction quiet.
 */
interface ProcessingRecord {
  dataController: { name: string; contact: string };
  dataSubject: { id: string; email: string };
  dataCategories: string[];
  processingPurposes: string[];
  legalBasis: string[];
  dataRecipients: string[];
  retentionPeriod: string;
  securityMeasures: string[];
  lastUpdated: string;
}

export const GDPRDataExport: React.FC = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [processingRecord, setProcessingRecord] =
    useState<ProcessingRecord | null>(null);
  const [showProcessingRecord, setShowProcessingRecord] = useState(false);

  const handleExport = async () => {
    try {
      setIsExporting(true);
      await gdprService.downloadDataExport();
      toast.success("✅ Data export downloaded successfully", {
        duration: 3000,
      });
    } catch (error: any) {
      console.error("Export failed:", error);
      toast.error("Failed to export data. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleShowProcessingRecord = async () => {
    try {
      // gdprService returns `unknown`; this is the API boundary.
      const record =
        (await gdprService.getProcessingRecord()) as ProcessingRecord;
      setProcessingRecord(record);
      setShowProcessingRecord(true);
    } catch (error: any) {
      console.error("Failed to get processing record:", error);
      toast.error("Failed to load processing record");
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="w-8 h-8 text-blue-600" />
          <h2 className="text-3xl font-bold text-gray-900">GDPR Data Export</h2>
        </div>
        <p className="text-gray-600">
          Download all your personal and health data in a machine-readable
          format. This is your right under GDPR Article 20 (Right to Data
          Portability).
        </p>
      </div>

      {/* Main Export Card */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-50 rounded-lg">
            <Download className="w-6 h-6 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Export Your Data
            </h3>
            <p className="text-gray-600 mb-4">
              Download a complete copy of all your data stored in HealthPass,
              including:
            </p>
            <ul className="space-y-2 text-sm text-gray-700 mb-6">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                Personal profile information
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                Health records (standardised format)
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                Body measurements and vital signs
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                Document metadata
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                Consent records and history
              </li>
            </ul>
            <button
              onClick={handleExport}
              disabled={isExporting}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <Download className="w-5 h-5" />
              {isExporting ? "Exporting..." : "Download My Data (JSON)"}
            </button>
          </div>
        </div>
      </div>

      {/* Processing Record Card */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-purple-50 rounded-lg">
            <FileText className="w-6 h-6 text-purple-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Data Processing Record
            </h3>
            <p className="text-gray-600 mb-4">
              View how we process your data (GDPR Article 30 compliance).
            </p>
            <button
              onClick={handleShowProcessingRecord}
              className="px-6 py-3 border border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50 transition-colors flex items-center gap-2"
            >
              <FileText className="w-5 h-5" />
              View Processing Record
            </button>
          </div>
        </div>

        {/* Processing Record Details */}
        {showProcessingRecord && processingRecord && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h4 className="font-semibold text-gray-900 mb-4">
              Data Processing Information
            </h4>

            <div className="space-y-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">
                  Data Controller:
                </span>
                <p className="text-gray-600">
                  {processingRecord.dataController.name}
                </p>
                <p className="text-gray-600 text-xs">
                  {processingRecord.dataController.contact}
                </p>
              </div>

              <div>
                <span className="font-medium text-gray-700">
                  Data Categories:
                </span>
                <ul className="list-disc list-inside text-gray-600 mt-1">
                  {processingRecord.dataCategories.map(
                    (cat: string, i: number) => (
                      <li key={i}>{cat}</li>
                    ),
                  )}
                </ul>
              </div>

              <div>
                <span className="font-medium text-gray-700">
                  Processing Purposes:
                </span>
                <ul className="list-disc list-inside text-gray-600 mt-1">
                  {processingRecord.processingPurposes.map(
                    (purpose: string, i: number) => (
                      <li key={i}>{purpose}</li>
                    ),
                  )}
                </ul>
              </div>

              <div>
                <span className="font-medium text-gray-700">Legal Basis:</span>
                <ul className="list-disc list-inside text-gray-600 mt-1">
                  {processingRecord.legalBasis.map(
                    (basis: string, i: number) => (
                      <li key={i}>{basis}</li>
                    ),
                  )}
                </ul>
              </div>

              <div>
                <span className="font-medium text-gray-700">
                  Retention Period:
                </span>
                <p className="text-gray-600">
                  {processingRecord.retentionPeriod}
                </p>
              </div>

              <div>
                <span className="font-medium text-gray-700">
                  Security Measures:
                </span>
                <ul className="list-disc list-inside text-gray-600 mt-1">
                  {processingRecord.securityMeasures.map(
                    (measure: string, i: number) => (
                      <li key={i}>{measure}</li>
                    ),
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Information Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-blue-800">
          <p className="font-medium mb-1">About Your Data Export</p>
          <p>
            The exported file contains all your data in JSON format, compliant
            with GDPR requirements. You can use this file to transfer your data
            to another service or keep a personal backup. The export includes
            timestamps and preserves the original structure of your data.
          </p>
        </div>
      </div>

      {/* GDPR Rights Summary */}
      <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
        <h4 className="font-semibold text-gray-900 mb-4">
          Your Data Rights (GDPR)
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="font-medium text-gray-700">✅ Right to Access</p>
            <p className="text-gray-600">View your data anytime in the app</p>
          </div>
          <div>
            <p className="font-medium text-gray-700">✅ Right to Portability</p>
            <p className="text-gray-600">Export your data (this page)</p>
          </div>
          <div>
            <p className="font-medium text-gray-700">
              ✅ Right to Rectification
            </p>
            <p className="text-gray-600">Edit your data through settings</p>
          </div>
          <div>
            <p className="font-medium text-gray-700">✅ Right to Erasure</p>
            <p className="text-gray-600">Delete your account in settings</p>
          </div>
          <div>
            <p className="font-medium text-gray-700">✅ Right to Restriction</p>
            <p className="text-gray-600">Manage consents in consent settings</p>
          </div>
          <div>
            <p className="font-medium text-gray-700">✅ Right to Object</p>
            <p className="text-gray-600">Withdraw consents anytime</p>
          </div>
        </div>
      </div>

      {/* Contact */}
      <div className="text-center text-sm text-gray-600">
        <p>
          Questions about your data? Contact our Data Protection Officer:{" "}
          <a
            href="mailto:dpo@travelhealthpass.app"
            className="text-blue-600 hover:underline"
          >
            dpo@travelhealthpass.app
          </a>
        </p>
      </div>
    </div>
  );
};
